// src/modules/operaciones/inventarios/InventariosNegativos.js 
import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { TEMAS_PLANOS, useTemaOscuro } from "../../../utils/tema";
import { fmt } from "../../../utils/format";
import { POR_PAGINA } from "./utils/negativosComun";
import useNegativos from "./hooks/useNegativos";
import ModalCausa from "./components/ModalCausaNegativo";

export default function InventariosNegativos({ onBackToMenu }) {
  const { items, loading, calculando, error, totalArticulos, progreso } = useNegativos();
  
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [seleccionado, setSeleccionado] = useState(null);
  const [orden, setOrden] = useState({ campo: null, dir: "asc" });
  const [temaOscuro, setTemaOscuro] = useTemaOscuro();

  const tema = temaOscuro ? TEMAS_PLANOS.oscuro : TEMAS_PLANOS.claro;

  const filtrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim();
    if (!q) return items;
    return items.filter(
      (i) => (i.ARTI_COD || "").toLowerCase().includes(q) || (i.ARTI_DES || "").toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const alternarOrden = useCallback((campo) => {
    setOrden((prev) => {
      if (prev.campo !== campo) return { campo, dir: "asc" };
      if (prev.dir === "asc") return { campo, dir: "desc" };
      return { campo: null, dir: "asc" };
    });
    setPagina(1);
  }, []);

  const filtradosOrdenados = useMemo(() => {
    if (!orden.campo) return filtrados;
    const f = orden.dir === "asc" ? 1 : -1;
    const { campo } = orden;
    const numerico = campo === "EXISTENCIA_TOTAL" || campo === "VALOR_NEGATIVO";
    return [...filtrados].sort((a, b) => {
      if (numerico) return (Number(a[campo]) - Number(b[campo])) * f;
      return String(a[campo] ?? "").localeCompare(String(b[campo] ?? ""), "es", { numeric: true }) * f;
    });
  }, [filtrados, orden]);

  const totalPag = Math.max(1, Math.ceil(filtradosOrdenados.length / POR_PAGINA));
  const pag = Math.min(pagina, totalPag);
  const desde = (pag - 1) * POR_PAGINA;
  const visibles = filtradosOrdenados.slice(desde, desde + POR_PAGINA);

  const COLUMNAS_ORDEN = [
    { campo: "ARTI_COD", label: "Código", align: "text-left" },
    { campo: "ARTI_DES", label: "Nombre", align: "text-left" },
    { campo: "EXISTENCIA_TOTAL", label: "Total", align: "text-right" },
    { campo: "VALOR_NEGATIVO", label: "Valor Neg.", align: "text-right" },
  ];

  const exportarExcel = () => {
    const wsData = filtradosOrdenados.map((a) => ({
      "Código": a.ARTI_COD,
      "Nombre": a.ARTI_DES,
      "Existencia Total": a.EXISTENCIA_TOTAL,
      "Tipo": a.TIPO === "TOTAL_NEGATIVO" ? "Negativo Total" : "Parcial",
      "Valor Negativo": a.VALOR_NEGATIVO,
      "Bodegas Negativas": a.BODEGAS_NEGATIVAS.map(b => b.BODE_COD).join(", "),
      "Fecha Origen": a.FECHA_ORIGEN_GLOBAL || "Sin registro",
      "Días en Negativo": a.DIAS_NEGATIVO
    }));
    
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Negativos");
    XLSX.writeFile(wb, `Negativos_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className={`min-h-screen ${tema.bgApp} flex flex-col p-4 sm:p-8 font-sans`}>
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center text-2xl shadow-lg shadow-red-600/30">📉</div>
          <div>
            <h1 className={`text-2xl font-extrabold ${tema.textMain}`}>Inventarios <span className="text-red-500">Negativos</span></h1>
            <p className={`${tema.textSub} text-sm mt-0.5`}>
              {calculando 
                ? "Calculando en Sysplus..." 
                : (totalArticulos > 1000 
                    ? `Mostrando los primeros 1000 de ${totalArticulos} artículo(s) con negativos` 
                    : `${totalArticulos} artículo(s) con negativos encontrados`)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={exportarExcel} className={`px-4 py-2.5 rounded-xl ${tema.bgBtn} ${tema.textMain} text-sm font-bold border ${tema.borderInput}`}>
            ⬇️ Exportar Excel
          </button>
          <button onClick={() => setTemaOscuro(!temaOscuro)} className={`p-2.5 rounded-xl ${tema.bgBtn} border ${tema.borderInput} ${tema.textMain}`}>
            {temaOscuro ? "☀️" : "🌙"}
          </button>
          <button onClick={onBackToMenu} className={`px-4 py-2.5 rounded-xl ${tema.bgBtn} ${tema.textMain} text-sm font-bold border ${tema.borderInput}`}>
            ← Volver al Menú
          </button>
        </div>
      </div>

      {/* BARRA DE PROGRESO */}
      {calculando && (
        <div className="mb-4 p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30 text-blue-900 shadow-sm flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-extrabold">
              El backend está recorriendo la base de datos... {progreso.porcentaje > 0 ? `(${progreso.porcentaje}%)` : ""}
            </p>
          </div>
          
          <div className="w-full h-2 bg-blue-900/10 rounded-full overflow-hidden mt-1">
            <div 
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${progreso.porcentaje || 0}%` }}
            />
          </div>
          <p className="text-xs text-blue-800/70 font-medium">
            Revisando artículo {progreso.procesados || 0} de {progreso.total || 0}...
          </p>
        </div>
      )}

      <div className="relative mb-4">
        <input
          type="text" placeholder="Buscar por código o nombre…" value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
          className={`w-full ${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-sm rounded-xl pl-10 pr-4 py-3`}
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2">🔍</span>
      </div>

      {error && <div className="mb-4 px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-bold">Error: {error}</div>}

      <div className="flex justify-between items-center mb-3 px-1">
        <p className={`${tema.textSub} text-xs font-bold`}>
          Página {pag} de {totalPag}
        </p>
        <div className="flex gap-2">
          {/* CORREGIDO: Botones con colores adaptados al tema oscuro */}
          <button 
            onClick={() => setPagina(Math.max(1, pag - 1))} 
            disabled={pag === 1} 
            className={`px-3 py-1.5 rounded-lg ${tema.bgBtn} border ${tema.borderInput} ${tema.textMain} disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold cursor-pointer transition-all hover:opacity-80`}
          >
            ‹ Anterior
          </button>
          <button 
            onClick={() => setPagina(Math.min(totalPag, pag + 1))} 
            disabled={pag === totalPag} 
            className={`px-3 py-1.5 rounded-lg ${tema.bgBtn} border ${tema.borderInput} ${tema.textMain} disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold cursor-pointer transition-all hover:opacity-80`}
          >
            Siguiente ›
          </button>
        </div>
      </div>

      <div className={`${tema.bgCard} rounded-2xl border ${tema.borderMain} shadow-xl overflow-hidden flex-1 flex flex-col`}>
        <div className="overflow-x-auto">
          <table className={`w-full text-left text-sm ${tema.textSub}`}>
            <thead className={`${tema.bgHeader} text-xs uppercase tracking-wider border-b ${tema.borderMain}`}>
              <tr>
                {COLUMNAS_ORDEN.map((c) => (
                  <th key={c.campo} className={`px-6 py-4 font-bold ${c.align}`}>
                    <button onClick={() => alternarOrden(c.campo)} className="hover:opacity-80 cursor-pointer">
                      {c.label} {orden.campo === c.campo && (orden.dir === "asc" ? "▲" : "▼")}
                    </button>
                  </th>
                ))}
                <th className="px-6 py-4 font-bold text-center">Acción</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${tema.borderMain}`}>
              {loading ? (
                <tr><td colSpan="5" className="px-6 py-12 text-center">Cargando...</td></tr>
              ) : visibles.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-12 text-center">No hay negativos registrados.</td></tr>
              ) : visibles.map((i) => (
                <tr key={i.ARTI_COD} className={`${tema.bgHover}`}>
                  <td className="px-6 py-4 font-mono text-purple-500 font-bold">{i.ARTI_COD}</td>
                  <td className={`px-6 py-4 max-w-[400px] truncate ${tema.textMain}`}>{i.ARTI_DES}</td>
                  <td className="px-6 py-4 text-right font-mono font-bold text-red-500">{fmt(i.EXISTENCIA_TOTAL)}</td>
                  <td className="px-6 py-4 text-right font-mono text-red-500">$ {fmt(i.VALOR_NEGATIVO)}</td>
                  <td className="px-6 py-4 text-center">
                    <button onClick={() => setSeleccionado(i)} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-purple-500/40 bg-purple-500/10 text-purple-500 hover:bg-purple-500/20">
                      🔎 Ver causa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {seleccionado && <ModalCausa articulo={seleccionado} onClose={() => setSeleccionado(null)} tema={tema} />}
    </div>
  );
}